import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
//own
import { createSx } from '../../../../../../../../../utils';
import IconButton from '../../../../../../../../IconButton';
import { useAttachmentsStateContext } from '../../../../../../../provider/AttachmentsStateProvider';
import { KEY_NAME } from '../../../../../../../../../../submodules/web-shared/enums/key-name';
import { Attachment } from '../../../../../../..';
import Footer from './components/Footer';
import NoImageAttachmentPreview from './components/NoImageAttachmentPreview';

const sx = createSx({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'neutral.outlineVariant',
    flex: 1,
  },
  container: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  btn: {
    transform: 'translateY(-50%)',
    zIndex: 9999,
    '& .left': {
      left: theme => theme.spacing(2)
    },
    '& .right': {
      right: theme => theme.spacing(2)
    }
  },
  previewContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  img: {
    width: 'inherit',
    maxWidth: 640,
    maxHeight: 820,
  },
});

const IMAGE_VIEWER_SETTINGS = {
  MIN_SCALE: 1,
  ROTATION_ANGLE: 90,
  TRANSLATE_POSITION: { x: 0, y: 0 },
};

interface Props<T extends Attachment, > {
  attachment: T;
}

const AttachmentPreviewSection = <T extends Attachment, >({ attachment }: Props<T>) => {
  const { t } = useTranslation();
  const [scale, setScale] = useState(IMAGE_VIEWER_SETTINGS.MIN_SCALE);
  const [rotation, setRotation] = useState(0);
  const [translate, setTranslate] = useState(IMAGE_VIEWER_SETTINGS.TRANSLATE_POSITION);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsError(false);
  }, [attachment]);

  const containerRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const isClicked = useRef<boolean>(false);
  const coords = useRef<{
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
  }>({
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0
  });

  const {
    moveToNext,
    moveToPrevious,
    isPrevDisabled,
    isNextDisabled,
  } = useAttachmentsStateContext();

  useEffect(() => {
    setTranslate(IMAGE_VIEWER_SETTINGS.TRANSLATE_POSITION);
    setScale(IMAGE_VIEWER_SETTINGS.MIN_SCALE);
    setRotation(0);
  }, [attachment]);

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1));
  };

  const handleZoomOut = () => {
    let currentScale = null;
    setScale((prevScale) => {
      const newScale = Math.max(prevScale - 0.1, IMAGE_VIEWER_SETTINGS.MIN_SCALE);
      currentScale = newScale;
      return newScale;
    });
    if (currentScale === IMAGE_VIEWER_SETTINGS.MIN_SCALE) {
      setTranslate(IMAGE_VIEWER_SETTINGS.TRANSLATE_POSITION);
    }
  };

  const handleRotateLeft = () => {
    setRotation((prevRotation) => prevRotation - IMAGE_VIEWER_SETTINGS.ROTATION_ANGLE);
  };

  const handleRotateRight = () => {
    setRotation((prevRotation) => prevRotation + IMAGE_VIEWER_SETTINGS.ROTATION_ANGLE);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      handleZoomOut();
    } else {
      handleZoomIn();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isClicked.current = true;
    coords.current.startX = e.clientX;
    coords.current.startY = e.clientY;
    coords.current.lastX = translate.x;
    coords.current.lastY = translate.y;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isClicked.current) return;
    const dx = e.clientX - coords.current.startX;
    const dy = e.clientY - coords.current.startY;
    setTranslate({ x: coords.current.lastX + dx, y: coords.current.lastY + dy });
  };

  const handleStop = () => {
    isClicked.current = false;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    isClicked.current = true;
    coords.current.startX = e.changedTouches[0].clientX;
    coords.current.startY = e.changedTouches[0].clientY;
    coords.current.lastX = translate.x;
    coords.current.lastY = translate.y;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isClicked.current) return;
    const dx = e.changedTouches[0].clientX - coords.current.startX;
    const dy = e.changedTouches[0].clientY - coords.current.startY;
    setTranslate({ x: coords.current.lastX + dx, y: coords.current.lastY + dy });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KEY_NAME.ARROW_LEFT && !isPrevDisabled) {
        moveToPrevious();
      }
      if (e.key === KEY_NAME.ARROW_RIGHT && !isNextDisabled) {
        moveToNext();
      }
      if (e.key === KEY_NAME.ARROW_UP) handleZoomIn();
      if (e.key === KEY_NAME.ARROW_DOWN) handleZoomOut();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPrevDisabled, isNextDisabled, attachment]);

  return (
    <Box sx={sx.root} onWheel={handleWheel}>
      <Box data-test-id='attachment-preview-section' sx={sx.container}>
        <IconButton
          title={t('label_previous')}
          onClick={moveToPrevious}
          disabled={isPrevDisabled}
          testID='navigate-previous-button'
          variant='contained'
          sx={sx.btn}
          classes={{ root: 'left' }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Box ref={containerRef} sx={sx.previewContainer} data-test-id='attachment-preview-content'>
          {!isError ? <Box
            ref={boxRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleStop}
            onMouseLeave={handleStop}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleStop}
            component='img'
            data-test-id='attachment-preview-image'
            sx={{
              ...sx.img,
              transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale}) rotate(${rotation}deg)`,
            }}
            src={attachment.url}
            alt={attachment.name}
            draggable={false}
            onError={() => {
              setIsError(true);
            }}
          />
            :
            <NoImageAttachmentPreview name={attachment.name} />}
        </Box>
        <IconButton
          title={t('next')}
          onClick={moveToNext}
          disabled={isNextDisabled}
          variant='contained'
          testID='navigate-next-button'
          sx={sx.btn}
          classes={{ root: 'right' }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
      <Footer
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        handleRotateLeft={handleRotateLeft}
        handleRotateRight={handleRotateRight}
        downloadUrl={attachment.download_url}
        attachmentName={attachment.name}
      />
    </Box>
  );
};

export default AttachmentPreviewSection;
